import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import isFinite from 'lodash/isFinite';

import { ChannelType } from 'api/ChannelsAPI';
import { ChannelOptNodeData, NodeId, NODE_TYPES, SendNodeData } from 'modules/workflows';
import { updateNode } from 'modules/workflows/ducks';
import { WarningBanner } from 'ui/banner';
import { ChannelOptEditorProps } from '../types';
import { fetchTemplateMessageChannel } from './CHOChannelEditor.gql';
import {
  getChannelClonedFromNodeData,
  getChannelEditorComponent,
  getChannelEditorProps,
  getUpdatedNodeData,
} from './CHOChannelEditor.utils';

export const CHOChannelEditor: FC<React.PropsWithChildren<ChannelOptEditorProps>> = (
  props: ChannelOptEditorProps,
) => {
  const { nodeData, resources = {}, node, clonedFromNodeData = {} } = props;
  const { selectedEditor = NODE_TYPES.SEND_EMAIL } = nodeData ?? ({} as ChannelOptNodeData);
  const dispatch = useDispatch();

  const Editor = useMemo(() => getChannelEditorComponent(selectedEditor), [selectedEditor]);
  const channelProps = useMemo(
    () => getChannelEditorProps(selectedEditor, node, resources),
    [selectedEditor, node, nodeData, resources],
  );
  const parsedClonedFromNodeData = useMemo(
    () => getChannelClonedFromNodeData(selectedEditor, clonedFromNodeData as ChannelOptNodeData),
    [selectedEditor, clonedFromNodeData],
  );

  const [templateId, setTemplateId] = useState<number | null | undefined>(
    channelProps?.nodeData?.templateId,
  );
  const [isWarningBannerVisible, setIsWarningBannerVisible] = useState<boolean>(false);

  const [getMessageChannel] = useLazyQuery(fetchTemplateMessageChannel, {
    fetchPolicy: 'no-cache',
    variables: { templateId },
    onCompleted: (resp) => {
      const { id, messageType } = resp?.templateAndContent || {};
      // Check if the template id has changed since this was called.
      // If it has, don't show the message as it is for the incorrect template.
      if (id === templateId) {
        const { channelType } = messageType?.messageChannel || {};
        setIsWarningBannerVisible(channelType === ChannelType.Transactional);
      } else {
        setIsWarningBannerVisible(false);
      }
    },
  });

  const onUpdate = (channelNodeData: SendNodeData, nodeId: NodeId) => {
    dispatch(
      updateNode(
        { nodeData: getUpdatedNodeData(nodeData, selectedEditor, channelNodeData) },
        nodeId,
      ),
    );
  };

  useEffect(() => {
    if (isFinite(templateId)) {
      getMessageChannel();
    } else if (isWarningBannerVisible) {
      setIsWarningBannerVisible(false);
    }
  }, [templateId]);

  return Editor ? (
    <Editor
      {...props}
      {...channelProps}
      clonedFromNodeData={parsedClonedFromNodeData}
      contentBanner={
        isWarningBannerVisible ? (
          <WarningBanner>
            Transactional templates are not supported by Channel Optimization. Please select a
            marketing template.
          </WarningBanner>
        ) : null
      }
      onTemplateIdUpdate={(tId?: number) => setTemplateId(tId)}
      onTemplateUpdate={() => templateId && getMessageChannel()}
      onUpdate={onUpdate}
    />
  ) : null;
};
